import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from "react";
import useModal from "../../Hooks/useModal";
import { Button } from "../Buttons";
import { Button as AntButton } from "antd";
import { CloseIcon } from "../Icons/iconImages";
import gsap from "gsap";

interface ModalProps {
  /**
   * @default undefined
   * Function to be called when the save button is clicked
   */
  onSave?: () => void;
  /**
   * @default undefined
   * Function to be called when the close button is clicked
   */
  onClose?: () => void;
  /**
   * @default undefined
   * The content of the modal
   */
  children: React.ReactNode;
  /**
   * @default "Allow"
   * The label of the save button
   */
  saveLabel?: string;
  /**
   * @default false
   * Whether to show the default footer with a close button
   */
  defaultFooter?: boolean;
  /**
   * @default ""
   * Extra class name for the modal container
   */
  className?: string;
  /**
   * @default undefined
   * Number of seconds to wait before closing the modal automatically
   */
  timer?: number;
  /**
   * @default true
   * Whether to close the modal when the backdrop is clicked
   */
  defaultClose?: boolean;
}

const Modal = ({
  onSave,
  onClose,
  children,
  saveLabel = "Allow",
  defaultFooter,
  className = '',
  timer,
  defaultClose = true,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const handleClose = () => {
    if (modalRef.current) {
      gsap.to(modalRef.current, {
        y: "100%",
        opacity: 0,
        duration: 0.3,
        ease: "power2.in",
        onComplete: () => {
          if (onClose) onClose();
          else if (onSave) onSave();
        },
      });
    }
  };

  useEffect(() => {
    gsap.fromTo(
      modalRef.current,
      { y: "100%", opacity: 0 },
      { y: "0%", opacity: 1, duration: 0.2, ease: "power1.out" }
    );
    gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2 });
  }, []);

  useEffect(() => {
    if (timer) {
      const timerId = setInterval(() => {
        handleClose();
      }, timer);
      return () => clearInterval(timerId);
    }
  }, [timer]);

  const modalContent = (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent black
      }}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-2xl shadow-lg p-6 relative ${className} overflow-hidden`}
        onClick={(e) => e.stopPropagation()} // Prevent click bubbling to backdrop
      >
        {defaultClose && (
          <AntButton
            className="absolute top-0 right-0 mt-2 mr-2"
            shape="circle"
            icon={<CloseIcon />}
            onClick={handleClose}
            type="text"
          />
        )}
        {children}
        {defaultFooter && (
          <div className="flex gap-2 justify-center mt-4">
            <Button variant="outlined" color="primary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="primary" onClick={onSave} disabled={!onSave}>
              {saveLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById('modal-root')!);
};

interface ModalBottomOverlayProps {
  onSave?: () => void;
  onClose: () => void;
  header: React.ReactNode;
  footer: React.ReactNode;
  children: React.ReactNode;
  isExpanded?: boolean;  // expansion state
  setIsExpanded?: React.Dispatch<React.SetStateAction<boolean>>;
  'data-tour'?: string;  // Tour identifier
}

const ModalBottomOverlay = ({
  onClose,
  header,
  footer,
  children,
  isExpanded = false, // Provide a default value
  setIsExpanded = () => { }, // Provide a default no-op function
  'data-tour': dataTour,
}: ModalBottomOverlayProps) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const [modalHeight, setModalHeight] = useState("auto");

  const handleClose = () => {
    gsap.to(modalRef.current, {
      y: "100%",
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        onClose();
        if (isExpanded) setIsExpanded(false);
      },
    });
  };

  useEffect(() => {
    // Animate modal and backdrop on mount
    gsap.fromTo(
      modalRef.current,
      { y: "100%", opacity: 0 },
      { y: "0%", opacity: 1, duration: 0.2, ease: "power1.out" }
    );
    gsap.fromTo(
      backdropRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.2 }
    );
  }, []);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    // modal height based on expansion
    const expandedHeight = "95vh";
    const collapsedHeight = "50vh";

    setModalHeight(isExpanded ? expandedHeight : collapsedHeight);

    const handleScroll = () => {
      if (isExpanded) return;
      const { scrollTop, scrollHeight, clientHeight } = modal;
      // Expand when almost scrolled to the bottom area
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        setIsExpanded(true);
      }
    };

    modal.addEventListener("scroll", handleScroll);

    // Clean-up on unmount
    return () => {
      modal.removeEventListener("scroll", handleScroll);
    };
  }, [isExpanded]);

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black-900 bg-opacity-50"
      onClick={(e) => {
        if (e.target === backdropRef.current) handleClose();
      }}
    >
      <div
        ref={modalRef}
        data-tour={dataTour}
        style={{
          height: modalHeight,
          maxHeight: modalHeight,
          overflowY: "auto",
          transition: "height 0.5s ease-in-out, max-height 0.5s ease-in-out",
        }}
        onClick={(e) => e.stopPropagation()}
        className="relative rounded-t-2xl bg-white shadow-lg xl:w-7/12 md:w-10/12 w-11/12"
      >
        <div className="header-container bg-gray-100 rounded-t-2xl p-4 sticky top-0 z-10">
          {header}
          <AntButton
            className="absolute top-0 right-0 mt-2 mr-2"
            shape="circle"
            icon={<CloseIcon />}
            onClick={handleClose}
            type="text"
          />
        </div>
        <div>{children}</div>
        {footer && <div className="bg-gray-100 p-4">{footer}</div>}
      </div>
    </div>
  );
};

export { Modal, ModalBottomOverlay };
